import { useState } from "react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE, resolveAccentPaletteColor } from "~/lib/accentPalette";
import { ColorSwatchGrid } from "~/pages/manifold-nextgen/components/ColorSwatchGrid";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import {
  PartialProject,
  mutateProjectList,
  projectPath,
  projectRouteId,
  updateProject,
} from "~/services/projects";

interface ProjectAppearanceDialogProps {
  project: PartialProject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProjectAppearanceDialog = ({
  project,
  open,
  onOpenChange,
}: ProjectAppearanceDialogProps) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Captured once per mount (the dialog remounts each time it opens), so it stays
  // stable as the Reset-equivalent baseline for the isDirty check below.
  const [committedColor] = useState(
    () => resolveAccentPaletteColor(project.accent_color ?? undefined).accent
  );
  const [color, setColor] = useState(committedColor);

  const isDirty = color !== committedColor;
  const previewColor = resolveAccentPaletteColor(color);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Mutate by the project's route id (short_id, falling back to the uuid), not
      // `project.id` directly — that's the cache key ProjectLayout's own fetch uses,
      // so this is what actually refreshes the overview page still showing this project.
      const routeId = projectRouteId(project);
      await updateProject(routeId, { accent_color: color });
      await Promise.all([mutate(projectPath(routeId)), mutateProjectList()]);
      showSuccess("Appearance saved");
      onOpenChange(false);
    } catch {
      showError("Failed to save appearance");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit appearance"
      contentClassName="sm:max-w-2xl"
      onPrimary={handleSave}
      primaryLabel="Save"
      primaryPendingLabel="Saving…"
      primaryDisabled={!isDirty}
      isPending={isSaving}
    >
      <div className="grid grid-cols-[280px_1fr] gap-8 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <div className="relative h-[246px] w-[200px] overflow-hidden rounded-xl rounded-l-lg border border-border bg-card">
            <div
              className="absolute inset-y-0 left-0 w-8 overflow-hidden rounded-l-lg"
              style={{ backgroundColor: previewColor.bg }}
            />
            <div className="ml-8 flex h-full flex-col gap-1 px-3 py-4">
              <p className="truncate text-sm font-semibold text-card-foreground">{project.name}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <ColorSwatchGrid colors={ACCENT_PALETTE} color={color} onChange={setColor} />
          </div>
        </div>
      </div>
    </FormDialog>
  );
};
